import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signalService } from "../../services";
import PortalRing from "../stories/PortalRing";
import SignalShareModal from "../SignalShareModal";
import SignalText from "../SignalText";
import { signalWindowLabel } from "../SignalCard";
import { audienceChip } from "../SignalInvitees";
import { Faces, RingButton, ShareGlyph, ago, errorMessage } from "./PeopleUI";
import { openedLine } from '../../utils/portalWords';

export default function PeoplePortalCard({
  signal,
  onUpdate,
  onDismiss,
  onViewPortal,
  onEventOpen,
  notify,
  leaving,
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [share, setShare] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  const mine = signal.isMine || signal.viewerState === "host";
  const joined = signal.viewerState === "accepted";
  const awaiting = signal.viewerState === "applied_pending";
  const minutes = Math.max(
    0,
    Math.ceil((new Date(signal.expiresAtUtc).getTime() - now) / 60000)
  );
  const expired = !!signal.expiresAtUtc && minutes <= 0;
  const windowLabel = signalWindowLabel(
    signal.dateTimeUtc,
    signal.expiresAtUtc
  );
  const room = audienceChip(signal);
  const faces =
    signal.acceptedPreview ||
    (signal.invitees || []).filter((p) => p.state === "accepted");
  const detail = () => navigate("/portals/" + signal.eventId);
  const join = async () => {
    if (pending.current || expired) return;
    pending.current = true;
    setBusy(true);
    try {
      const result = await signalService.imDown(signal.eventId);
      if (!result.success)
        throw new Error(errorMessage(result, "Could not join this portal."));
      onUpdate(result.data);
      notify(
        result.data?.viewerState === "accepted" ? "You're in" : "Request sent"
      );
    } catch (e) {
      notify(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <article className={`people-portal ${leaving ? "people-leaving" : ""}`}>
      <div className="people-portal-header">
        <PortalRing
          size={46}
          state="live"
          avatarUrl={signal.hostAvatarUrl}
          initial={(signal.hostDisplayName || "?")[0]}
          onClick={(e) => onViewPortal(signal.hostUserId, e, signal.eventId)}
          ariaLabel={`View ${signal.hostDisplayName}'s portal`}
        />
        <div className="people-portal-author">
          <b>{signal.hostDisplayName}</b> opened a portal
          <div className="people-meta">
            {[
              ago(signal.createdAtUtc),
              signal.primaryCityLabel || signal.locationLabel,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        {signal.expiresAtUtc && (
          <span className="people-time-left">
            {expired
              ? "Ended"
              : `${
                  minutes >= 60 ? Math.ceil(minutes / 60) + "H" : minutes + "M"
                } left`}
          </span>
        )}
      </div>
      <button type="button" onClick={detail} className="people-quote">
        {signal.signalText ? <>“<SignalText text={signal.signalText} />”</> : openedLine(signal.hostDisplayName)}
      </button>
      {windowLabel && <div className="people-window">{windowLabel}</div>}
      {signal.sourceEvent && (
        <button
          type="button"
          className="people-source-event"
          onClick={() => onEventOpen(signal.sourceEvent)}
        >
          {signal.sourceEvent.posterUrl && (
            <img
              src={signal.sourceEvent.posterUrl}
              alt=""
              referrerPolicy="no-referrer"
            />
          )}
          <span>
            <small>
              The event
              {signal.sourceEvent.isUpcoming === false ? " · Past" : ""}
            </small>
            <b>{signal.sourceEvent.title}</b>
          </span>
          <i>→</i>
        </button>
      )}
      <div className="people-portal-footer">
        <Faces people={faces} />
        <span>
          {signal.acceptedCount ? `${signal.acceptedCount} in` : "No one yet"}
        </span>
        {room && (
          <span className="people-room" data-room={room.key}>
            {room.label}
          </span>
        )}
      </div>
      <div className="people-portal-actions">
        <button
          type="button"
          className={`people-pill ${joined ? "is-done" : ""}`}
          disabled={busy || (!mine && (awaiting || expired))}
          onClick={mine || joined ? detail : join}
        >
          {busy
            ? "Joining…"
            : mine
            ? "Manage"
            : joined
            ? "You're in"
            : awaiting
            ? "Asked to join"
            : expired
            ? "Ended"
            : "I'm in"}
        </button>
        <RingButton onClick={() => setShare(true)} label="Share portal">
          <ShareGlyph />
        </RingButton>
        {!mine && (
          <button
            type="button"
            className="people-dismiss"
            disabled={leaving}
            onClick={() => onDismiss(signal)}
            aria-label={`Dismiss portal from ${signal.hostDisplayName}`}
          >
            ×
          </button>
        )}
      </div>
      {share && (
        <SignalShareModal signal={signal} onClose={() => setShare(false)} />
      )}
    </article>
  );
}
